import { Fragment } from 'react'
import { chartSequentialToken } from '../ChartSeriesTokens'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'

export interface HeatmapAxis {
  key: string
  label: string
}

export interface HeatmapCell {
  rowKey: string
  colKey: string
  /** Never negative; a real zero value (e.g. AC3.1's zero-fill semantic) is a real data point, never omitted. */
  value: number
}

export interface HeatmapChartProps {
  title: string
  /** Row axis (e.g. jurisdiction). */
  rows: HeatmapAxis[]
  /** Column axis (e.g. fiscal year). */
  columns: HeatmapAxis[]
  /** One entry per (row, col) the caller has a value for — a missing pair renders as 0 (the caller's own zero-fill mapper decides whether that ever actually happens). */
  cells: HeatmapCell[]
  valueFormatter?: (value: number) => string
  onCellClick?: (rowKey: string, colKey: string) => void
  height?: number
}

/**
 * IB-282 View 3, AC3.1 — "Credit value by state × FY" heatmap (RAID R-22, ratified by IB-291's
 * sequential-ramp tokens, `--iw-chart-seq-1…6` via {@link chartSequentialToken} — never a raw hex or
 * an ad hoc unratified ramp). No Recharts primitive maps cleanly onto a 2-axis intensity grid, so
 * this renders as a plain CSS grid of colour cells (same `ChartFrame`/`ChartAccessibleTable`
 * data-table-toggle contract every other chart in `charts/` uses, design-system.md §5.3) — zero
 * animation, so it is deterministic by construction (no `isAnimationActive` flakiness to guard
 * against, unlike the Recharts-based wrappers).
 *
 * Colour intensity is the cell's value's position within `[min, max]` across ALL cells, quantized
 * onto the 6 ratified ramp stops — a real `0` (AC3.1's zero-fill semantic, `CreditValueAssembler`
 * AC25) still renders as the coolest/palest stop, never omitted from the grid.
 */
export function HeatmapChart({
  title,
  rows,
  columns,
  cells,
  valueFormatter = (v) => String(v),
  onCellClick,
  height = 280,
}: HeatmapChartProps) {
  const byKey = new Map(cells.map((c) => [`${c.rowKey}::${c.colKey}`, c.value]))
  const values = cells.map((c) => c.value)
  const min = values.length > 0 ? Math.min(...values) : 0
  const max = values.length > 0 ? Math.max(...values) : 0

  function tokenFor(value: number): string {
    if (max === min) return chartSequentialToken(0, 1)
    const step = Math.round(((value - min) / (max - min)) * 5)
    return chartSequentialToken(step, 6)
  }

  const table = (
    <ChartAccessibleTable
      caption={`${title} — data table`}
      columns={[
        { key: 'row', label: 'Jurisdiction' },
        ...columns.map((c) => ({ key: c.key, label: c.label, align: 'end' as const })),
      ]}
      rows={rows.map((r) => {
        const out: Record<string, string> = { row: r.label }
        for (const c of columns) out[c.key] = valueFormatter(byKey.get(`${r.key}::${c.key}`) ?? 0)
        return out
      })}
    />
  )

  const chart =
    rows.length === 0 || columns.length === 0 ? (
      <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>No data for the current filter.</p>
    ) : (
      <div
        role="grid"
        aria-label={title}
        style={{
          display: 'grid',
          gridTemplateColumns: `140px repeat(${columns.length}, minmax(48px, 1fr))`,
          gap: 2,
          height: '100%',
          overflow: 'auto',
        }}
      >
        <div />
        {columns.map((c) => (
          <div
            key={c.key}
            style={{ fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-1) 0' }}
          >
            {c.label}
          </div>
        ))}
        {rows.map((r) => (
          <Fragment key={r.key}>
            <div style={{ fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)', display: 'flex', alignItems: 'center' }}>
              {r.label}
            </div>
            {columns.map((c) => {
              const value = byKey.get(`${r.key}::${c.key}`) ?? 0
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => onCellClick?.(r.key, c.key)}
                  aria-label={`${r.label}, ${c.label}: ${valueFormatter(value)}`}
                  title={`${r.label} · ${c.label}: ${valueFormatter(value)}`}
                  style={{
                    background: tokenFor(value),
                    border: 'none',
                    borderRadius: 'var(--iw-radius-sm)',
                    minHeight: 32,
                    cursor: onCellClick ? 'pointer' : 'default',
                  }}
                />
              )
            })}
          </Fragment>
        ))}
      </div>
    )

  return <ChartFrame title={title} height={height} table={table} chart={chart} />
}
